import React, { useState, useEffect, useMemo, useRef } from 'react';
import Icon from '../../../components/AppIcon';
import { supabase } from '../../../lib/supabase';

const ALL_COLUMNS = [
  { key: 'name', label: 'Name', sortable: true, required: true },
  { key: 'identifier', label: 'ID / Identifier', sortable: true },
  { key: 'category', label: 'Category', sortable: true },
  { key: 'status', label: 'Status', sortable: true },
  { key: 'currentAssignment', label: 'Current Assignment', sortable: false },
  { key: 'upcomingAssignments', label: 'Upcoming', sortable: true },
  { key: 'conflicts', label: 'Conflicts', sortable: true },
];

const DEFAULT_VISIBLE = ['name', 'identifier', 'category', 'status', 'currentAssignment', 'upcomingAssignments'];
const DEFAULT_FILTERS = { search: '', category: 'all', status: 'all' };
const DEFAULT_SORT = { column: 'name', direction: 'asc' };

const statusConfig = {
  'Assigned': { color: 'text-blue-600', bg: 'bg-blue-100', icon: 'Briefcase' },
  'Available': { color: 'text-green-600', bg: 'bg-green-100', icon: 'CheckCircle2' },
  'Maintenance': { color: 'text-yellow-600', bg: 'bg-yellow-100', icon: 'Wrench' },
};

const ResourceListView = ({ resources, categories, onResourceClick, filters: externalFilters, onFilterChange, onBulkEdit, onBulkDelete, onBulkAssign }) => {
  const [visibleColumns, setVisibleColumns] = useState(DEFAULT_VISIBLE);
  const [sort, setSort] = useState(DEFAULT_SORT);
  const [localFilters, setLocalFilters] = useState(DEFAULT_FILTERS);
  const [showColumnPicker, setShowColumnPicker] = useState(false);
  const [prefsLoaded, setPrefsLoaded] = useState(false);
  const [saving, setSaving] = useState(false);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const columnPickerRef = useRef(null);

  // Sync localFilters with externalFilters when they change
  useEffect(() => {
    if (!externalFilters) return;
    // Map external filter status values (lowercase/dashed) to internal exact-case values
    const statusMap = {
      'all': 'all',
      'assigned': 'Assigned',
      'available': 'Available',
      'in-use': 'Assigned',
      'maintenance': 'Maintenance',
      'unavailable': 'Maintenance',
      'conflicts': 'conflicts',
    };
    const mappedStatus = statusMap?.[externalFilters?.status] ?? externalFilters?.status ?? 'all';
    setLocalFilters(prev => ({
      ...prev,
      search: externalFilters?.search ?? prev?.search,
      category: externalFilters?.category ?? prev?.category,
      status: mappedStatus,
    }));
  }, [externalFilters?.status, externalFilters?.category, externalFilters?.search]);

  // Load preferences from Supabase on mount
  useEffect(() => {
    loadPreferences();
  }, []);

  // Close column picker on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (columnPickerRef?.current && !columnPickerRef?.current?.contains(e?.target)) {
        setShowColumnPicker(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const loadPreferences = async () => {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) return;

      const { data, error } = await supabase
        ?.from('user_resource_list_preferences')
        ?.select('*')
        ?.eq('user_id', user?.id)
        ?.single();

      if (data && !error) {
        setVisibleColumns(data?.visible_columns || DEFAULT_VISIBLE);
        setLocalFilters(data?.saved_filters || DEFAULT_FILTERS);
        setSort({ column: data?.sort_column || 'name', direction: data?.sort_direction || 'asc' });
        // Sync external filters
        if (onFilterChange && data?.saved_filters) {
          onFilterChange(data?.saved_filters);
        }
      }
    } catch (err) {
      // Preferences not found, use defaults
    } finally {
      setPrefsLoaded(true);
    }
  };

  const savePreferences = async (newVisible, newFilters, newSort) => {
    setSaving(true);
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) return;

      const payload = {
        user_id: user?.id,
        visible_columns: newVisible,
        saved_filters: newFilters,
        sort_column: newSort?.column,
        sort_direction: newSort?.direction,
      };

      await supabase
        ?.from('user_resource_list_preferences')
        ?.upsert(payload, { onConflict: 'user_id' });
    } catch (err) {
      console.error('Failed to save preferences:', err?.message);
    } finally {
      setSaving(false);
    }
  };

  const handleSortChange = (colKey) => {
    const newSort = sort?.column === colKey
      ? { column: colKey, direction: sort?.direction === 'asc' ? 'desc' : 'asc' }
      : { column: colKey, direction: 'asc' };
    setSort(newSort);
    savePreferences(visibleColumns, localFilters, newSort);
  };

  const handleFilterChange = (key, value) => {
    const newFilters = { ...localFilters, [key]: value };
    setLocalFilters(newFilters);
    if (onFilterChange) onFilterChange(newFilters);
    savePreferences(visibleColumns, newFilters, sort);
  };

  const handleResetFilters = () => {
    setLocalFilters(DEFAULT_FILTERS);
    if (onFilterChange) onFilterChange(DEFAULT_FILTERS);
    savePreferences(visibleColumns, DEFAULT_FILTERS, sort);
  };

  const handleToggleColumn = (colKey) => {
    const col = ALL_COLUMNS?.find(c => c?.key === colKey);
    if (col?.required) return;
    const newVisible = visibleColumns?.includes(colKey)
      ? visibleColumns?.filter(k => k !== colKey)
      : [...visibleColumns, colKey];
    setVisibleColumns(newVisible);
    savePreferences(newVisible, localFilters, sort);
  };

  const activeColumns = ALL_COLUMNS?.filter(c => visibleColumns?.includes(c?.key));

  const categoryOptions = [
    { value: 'all', label: 'All Categories' },
    ...(categories || [])?.map(c => ({ value: c?.id, label: c?.name }))
  ];

  const statusOptions = [
    { value: 'all', label: 'All Status' },
    { value: 'Assigned', label: 'Assigned' },
    { value: 'Available', label: 'Available' },
    { value: 'Maintenance', label: 'Maintenance' },
  ];

  const filteredResources = useMemo(() => {
    let result = [...(resources || [])];

    if (localFilters?.search) {
      const q = localFilters?.search?.toLowerCase();
      result = result?.filter(r =>
        r?.name?.toLowerCase()?.includes(q) ||
        r?.identifier?.toLowerCase()?.includes(q)
      );
    }
    if (localFilters?.category !== 'all') {
      result = result?.filter(r => r?.categoryId === localFilters?.category);
    }
    if (localFilters?.status !== 'all') {
      result = result?.filter(r => r?.status === localFilters?.status);
    }

    // Sort
    result?.sort((a, b) => {
      let aVal, bVal;
      switch (sort?.column) {
        case 'name': aVal = a?.name || ''; bVal = b?.name || ''; break;
        case 'identifier': aVal = a?.identifier || ''; bVal = b?.identifier || ''; break;
        case 'category': aVal = a?.category || ''; bVal = b?.category || ''; break;
        case 'status': aVal = a?.status || ''; bVal = b?.status || ''; break;
        case 'upcomingAssignments': aVal = a?.upcomingAssignments?.length || 0; bVal = b?.upcomingAssignments?.length || 0; break;
        case 'conflicts': aVal = a?.conflicts?.length || 0; bVal = b?.conflicts?.length || 0; break;
        default: aVal = a?.name || ''; bVal = b?.name || '';
      }
      if (typeof aVal === 'string') {
        return sort?.direction === 'asc' ? aVal?.localeCompare(bVal) : bVal?.localeCompare(aVal);
      }
      return sort?.direction === 'asc' ? aVal - bVal : bVal - aVal;
    });

    return result;
  }, [resources, localFilters, sort]);

  const hasActiveFilters = localFilters?.search || localFilters?.category !== 'all' || localFilters?.status !== 'all';

  // Selection helpers
  const allVisibleIds = filteredResources?.map(r => r?.id);
  const allSelected = allVisibleIds?.length > 0 && allVisibleIds?.every(id => selectedIds?.has(id));
  const someSelected = allVisibleIds?.some(id => selectedIds?.has(id)) && !allSelected;
  const selectedCount = [...selectedIds]?.filter(id => allVisibleIds?.includes(id))?.length;

  const handleSelectAll = () => {
    if (allSelected) {
      setSelectedIds(prev => {
        const next = new Set(prev);
        allVisibleIds?.forEach(id => next?.delete(id));
        return next;
      });
    } else {
      setSelectedIds(prev => {
        const next = new Set(prev);
        allVisibleIds?.forEach(id => next?.add(id));
        return next;
      });
    }
  };

  const handleSelectRow = (e, id) => {
    e?.stopPropagation();
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next?.has(id)) next?.delete(id);
      else next?.add(id);
      return next;
    });
  };

  const handleClearSelection = () => setSelectedIds(new Set());

  const handleBulkEdit = () => {
    const selected = filteredResources?.filter(r => selectedIds?.has(r?.id));
    onBulkEdit?.(selected);
  };

  const handleBulkDelete = () => {
    const selected = filteredResources?.filter(r => selectedIds?.has(r?.id));
    onBulkDelete?.(selected);
  };

  const SortIcon = ({ colKey }) => {
    if (sort?.column !== colKey) return <Icon name="ChevronsUpDown" size={14} className="text-muted-foreground/50 ml-1" />;
    return sort?.direction === 'asc'
      ? <Icon name="ChevronUp" size={14} className="text-accent ml-1" />
      : <Icon name="ChevronDown" size={14} className="text-accent ml-1" />;
  };

  return (
    <div className="space-y-4">
      {/* Filter Bar */}
      <div className="bg-card rounded-xl border border-border p-4">
        <div className="flex flex-col md:flex-row gap-3 items-start md:items-center">
          {/* Search */}
          <div className="relative flex-1 min-w-0">
            <Icon name="Search" size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <input
              type="text"
              placeholder="Search by name or ID..."
              value={localFilters?.search}
              onChange={e => handleFilterChange('search', e?.target?.value)}
              className="w-full pl-9 pr-3 py-2 text-sm border border-border rounded-lg bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-accent/30 focus:border-accent"
            />
          </div>

          {/* Category Filter */}
          <select
            value={localFilters?.category}
            onChange={e => handleFilterChange('category', e?.target?.value)}
            className="px-3 py-2 text-sm border border-border rounded-lg bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-accent/30 focus:border-accent min-w-[160px]"
          >
            {categoryOptions?.map(opt => (
              <option key={opt?.value} value={opt?.value}>{opt?.label}</option>
            ))}
          </select>

          {/* Status Filter */}
          <select
            value={localFilters?.status}
            onChange={e => handleFilterChange('status', e?.target?.value)}
            className="px-3 py-2 text-sm border border-border rounded-lg bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-accent/30 focus:border-accent"
          >
            {statusOptions?.map(opt => (
              <option key={opt?.value} value={opt?.value}>{opt?.label}</option>
            ))}
          </select>

          {/* Reset Filters */}
          {hasActiveFilters && (
            <button
              onClick={handleResetFilters}
              className="flex items-center gap-1.5 px-3 py-2 text-sm text-muted-foreground hover:text-foreground border border-border rounded-lg hover:bg-muted/50 transition-colors whitespace-nowrap"
            >
              <Icon name="X" size={14} />
              Clear
            </button>
          )}

          {/* Column Picker */}
          <div className="relative" ref={columnPickerRef}>
            <button
              onClick={() => setShowColumnPicker(v => !v)}
              className="flex items-center gap-1.5 px-3 py-2 text-sm border border-border rounded-lg bg-background hover:bg-muted/50 text-foreground transition-colors whitespace-nowrap"
            >
              <Icon name="Columns3" size={14} />
              Columns
              {saving && <Icon name="Loader2" size={12} className="animate-spin text-muted-foreground" />}
            </button>

            {showColumnPicker && (
              <div className="absolute right-0 top-full mt-1 z-50 bg-card border border-border rounded-xl shadow-lg p-3 min-w-[200px]">
                <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2">Toggle Columns</p>
                {ALL_COLUMNS?.map(col => (
                  <label
                    key={col?.key}
                    className={`flex items-center gap-2 px-2 py-1.5 rounded-lg cursor-pointer hover:bg-muted/50 transition-colors ${
                      col?.required ? 'opacity-50 cursor-not-allowed' : ''
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={visibleColumns?.includes(col?.key)}
                      onChange={() => handleToggleColumn(col?.key)}
                      disabled={col?.required}
                      className="rounded border-border text-accent focus:ring-accent"
                    />
                    <span className="text-sm text-foreground">{col?.label}</span>
                    {col?.required && <span className="text-xs text-muted-foreground ml-auto">(required)</span>}
                  </label>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
      {/* Results count + bulk action bar */}
      <div className="flex items-center justify-between">
        <p className="text-sm text-muted-foreground">
          {filteredResources?.length} resource{filteredResources?.length !== 1 ? 's' : ''}
          {hasActiveFilters ? ' (filtered)' : ''}
        </p>
      </div>

      {/* Bulk Action Toolbar */}
      {selectedCount > 0 && (
        <div className="flex items-center gap-3 px-4 py-3 bg-accent/5 border border-accent/20 rounded-xl">
          <div className="flex items-center gap-2 flex-1">
            <div className="w-6 h-6 rounded-full bg-accent flex items-center justify-center">
              <span className="text-xs font-bold text-white">{selectedCount}</span>
            </div>
            <span className="text-sm font-medium text-foreground">
              {selectedCount} resource{selectedCount !== 1 ? 's' : ''} selected
            </span>
          </div>
          <button
            onClick={() => onBulkAssign?.(filteredResources?.filter(r => selectedIds?.has(r?.id)))}
            className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
          >
            <Icon name="UserCheck" size={14} />
            Bulk Assign
          </button>
          <button
            onClick={handleBulkEdit}
            className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium bg-accent text-white rounded-lg hover:bg-accent/90 transition-colors"
          >
            <Icon name="PencilLine" size={14} />
            Bulk Edit
          </button>
          <button
            onClick={handleBulkDelete}
            className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium bg-destructive text-white rounded-lg hover:bg-destructive/90 transition-colors"
          >
            <Icon name="Trash2" size={14} />
            Delete
          </button>
          <button
            onClick={handleClearSelection}
            className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-muted-foreground hover:text-foreground border border-border rounded-lg hover:bg-muted/50 transition-colors"
          >
            <Icon name="X" size={14} />
            Clear
          </button>
        </div>
      )}

      {/* Table */}
      <div className="bg-card rounded-xl border border-border overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border bg-muted/30">
                {/* Select All Checkbox */}
                <th className="px-4 py-3 w-10">
                  <input
                    type="checkbox"
                    checked={allSelected}
                    ref={el => { if (el) el.indeterminate = someSelected; }}
                    onChange={handleSelectAll}
                    className="rounded border-border text-accent focus:ring-accent cursor-pointer"
                    title={allSelected ? 'Deselect all' : 'Select all'}
                  />
                </th>
                {activeColumns?.map(col => (
                  <th
                    key={col?.key}
                    className={`px-4 py-3 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wide whitespace-nowrap ${
                      col?.sortable ? 'cursor-pointer hover:text-foreground select-none' : ''
                    }`}
                    onClick={() => col?.sortable && handleSortChange(col?.key)}
                  >
                    <span className="flex items-center">
                      {col?.label}
                      {col?.sortable && <SortIcon colKey={col?.key} />}
                    </span>
                  </th>
                ))}
                <th className="px-4 py-3 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wide">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {filteredResources?.length === 0 ? (
                <tr>
                  <td colSpan={activeColumns?.length + 2} className="px-4 py-12 text-center">
                    <div className="flex flex-col items-center gap-2">
                      <Icon name="PackageSearch" size={32} className="text-muted-foreground/40" />
                      <p className="text-muted-foreground text-sm">
                        {hasActiveFilters ? 'No resources match your filters.' : 'No resources found.'}
                      </p>
                      {hasActiveFilters && (
                        <button
                          onClick={handleResetFilters}
                          className="text-accent text-sm hover:underline"
                        >
                          Clear filters
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ) : (
                filteredResources?.map(resource => (
                  <tr
                    key={resource?.id}
                    className={`hover:bg-muted/30 transition-colors cursor-pointer ${
                      selectedIds?.has(resource?.id) ? 'bg-accent/5' : ''
                    }`}
                    onClick={() => onResourceClick?.(resource)}
                  >
                    {/* Row Checkbox */}
                    <td className="px-4 py-3 w-10" onClick={e => handleSelectRow(e, resource?.id)}>
                      <input
                        type="checkbox"
                        checked={selectedIds?.has(resource?.id)}
                        onChange={() => {}}
                        className="rounded border-border text-accent focus:ring-accent cursor-pointer"
                      />
                    </td>
                    {activeColumns?.map(col => (
                      <td key={col?.key} className="px-4 py-3">
                        {col?.key === 'name' && (
                          <span className="font-medium text-foreground capitalize">{resource?.name}</span>
                        )}
                        {col?.key === 'identifier' && (
                          <span className="text-muted-foreground font-mono text-xs">{resource?.identifier || '—'}</span>
                        )}
                        {col?.key === 'category' && (
                          <span className="text-foreground capitalize">{resource?.category || '—'}</span>
                        )}
                        {col?.key === 'status' && (() => {
                          const cfg = statusConfig?.[resource?.status] || statusConfig?.['Available'];
                          const hasConflicts = resource?.conflicts?.length > 0;
                          return (
                            <div className="flex items-center gap-1.5">
                              <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${cfg?.bg} ${cfg?.color}`}>
                                <Icon name={cfg?.icon} size={12} />
                                {resource?.status}
                              </span>
                              {hasConflicts && (
                                <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full bg-red-100 text-red-600 text-xs font-semibold border border-red-200" title={`${resource?.conflicts?.length} scheduling conflict${resource?.conflicts?.length !== 1 ? 's' : ''}`}>
                                  <Icon name="AlertTriangle" size={10} />
                                  {resource?.conflicts?.length}
                                </span>
                              )}
                            </div>
                          );
                        })()}
                        {col?.key === 'currentAssignment' && (
                          resource?.currentAssignment ? (
                            <div className="min-w-0">
                              <p className="text-foreground text-xs font-medium truncate max-w-[180px]">
                                {resource?.currentAssignment?.projectName}
                              </p>
                              <p className="text-muted-foreground text-xs truncate max-w-[180px]">
                                {resource?.currentAssignment?.phaseName}
                              </p>
                            </div>
                          ) : (
                            <span className="text-muted-foreground text-xs">—</span>
                          )
                        )}
                        {col?.key === 'upcomingAssignments' && (
                          <span className="text-foreground text-xs">
                            {resource?.upcomingAssignments?.length > 0
                              ? `${resource?.upcomingAssignments?.length} upcoming`
                              : '—'}
                          </span>
                        )}
                        {col?.key === 'conflicts' && (
                          resource?.conflicts?.length > 0 ? (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-red-100 text-red-600 text-xs font-medium">
                              <Icon name="AlertTriangle" size={11} />
                              {resource?.conflicts?.length}
                            </span>
                          ) : (
                            <span className="text-muted-foreground text-xs">—</span>
                          )
                        )}
                      </td>
                    ))}
                    <td className="px-4 py-3">
                      <button
                        onClick={e => { e?.stopPropagation(); onResourceClick?.(resource); }}
                        className="text-muted-foreground hover:text-accent transition-colors"
                        title="View details"
                      >
                        <Icon name="ChevronRight" size={16} />
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default ResourceListView;
